/**
 * Recherche dans les pages « Mes contacts » (tuteur et élève) — filtrage 100 %
 * côté client des listes déjà chargées, sans requête réseau.
 *
 * Règles (testées) :
 *  - insensible à la casse ET aux accents (« elodie » trouve « Élodie ») ;
 *  - tirets, apostrophes et espaces multiples sont équivalents à un espace
 *    (« jean luc » trouve « Jean-Luc ») ;
 *  - plusieurs mots = ET : chaque mot doit apparaître quelque part dans les champs
 *    de la ligne (nom, niveau, matières…), dans n'importe quel ordre ;
 *  - une recherche vide ne filtre rien.
 */

/** Minuscules, sans accents, séparateurs ramenés à un espace. */
export function normalizeSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[-'’_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Vrai si la saisie contient quelque chose à chercher (espaces seuls = non). */
export function hasSearchQuery(query: string): boolean {
  return normalizeSearch(query).length > 0;
}

/** Vrai si tous les mots de `query` se retrouvent dans l'ensemble des `fields`. */
export function matchesQuery(query: string, ...fields: Array<string | null | undefined>): boolean {
  const terms = normalizeSearch(query).split(' ').filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = normalizeSearch(fields.filter((f): f is string => !!f).join(' '));
  return terms.every((term) => haystack.includes(term));
}

/** Filtre une liste ; `fields` dit dans quels textes de chaque élément chercher. */
export function filterByQuery<T>(
  items: readonly T[],
  query: string,
  fields: (item: T) => Array<string | null | undefined>,
): T[] {
  if (!hasSearchQuery(query)) return [...items];
  return items.filter((item) => matchesQuery(query, ...fields(item)));
}
